<script>
import { Checkbox } from "@/shared/ui/checkbox";

export default {
  name: "TreeNode",
  components: { Checkbox },
  props: {
    node: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    parentLines: { type: Array, default: () => [] },
    isLast: { type: Boolean, default: false },
    isRoot: { type: Boolean, default: false },
    nodeKey: { type: String, default: "id" },
    labelKey: { type: String, default: "label" },
    childrenKey: { type: String, default: "children" },
    selectedId: { type: [String, Number, null], default: null },
    checkedIds: { type: Array, default: () => [] },
    expandedIds: { type: Array, default: () => [] },
    showCheckbox: { type: Boolean, default: true },
  },
  emits: ["select", "toggle-expand", "toggle-check"],
  computed: {
    id() {
      return this.node?.[this.nodeKey];
    },
    label() {
      return this.node?.[this.labelKey] ?? "";
    },
    children() {
      return this.node?.[this.childrenKey] ?? [];
    },
    hasChildren() {
      return Array.isArray(this.children) && this.children.length > 0;
    },
    isExpanded() {
      return this.expandedIds.includes(this.id);
    },
    isSelected() {
      return this.selectedId === this.id;
    },
    isChecked() {
      return this.checkedIds.includes(this.id);
    },
    childParentLines() {
      if (this.isRoot) return [];
      return [...this.parentLines, !this.isLast];
    },
  },
  methods: {
    toggleExpand() {
      if (!this.hasChildren) return;
      this.$emit("toggle-expand", this.id);
    },
    select() {
      this.$emit("select", this.node);
    },
    onCheckChange(val) {
      this.$emit("toggle-check", { node: this.node, checked: val });
    },
    onChildSelect(node) {
      this.$emit("select", node);
    },
    onChildToggleExpand(id) {
      this.$emit("toggle-expand", id);
    },
    onChildToggleCheck(payload) {
      this.$emit("toggle-check", payload);
    },
  },
};
</script>

<template>
  <div class="tree-node">
    <div
      class="tree-node__row"
      :class="{
        'tree-node__row--selected': isSelected,
        'tree-node__row--root': isRoot,
      }"
      @click="select"
    >
      <div
        v-for="(hasLine, i) in parentLines"
        :key="'a' + i"
        class="tree-node__channel"
        :class="{ 'tree-node__channel--line': hasLine }"
      />

      <div v-if="!isRoot" class="tree-node__channel tree-node__channel--corner">
        <div class="tree-node__line tree-node__line--v-top"></div>
        <div
          v-if="!isLast"
          class="tree-node__line tree-node__line--v-bottom"
        ></div>
        <div class="tree-node__line tree-node__line--h"></div>
        <button
          v-if="hasChildren"
          type="button"
          class="tree-node__toggle"
          :aria-label="isExpanded ? $t('Свернуть') : $t('Раскрыть')"
          @click.stop="toggleExpand"
        >
          {{ isExpanded ? "−" : "+" }}
        </button>
      </div>

      <div v-if="isRoot" class="tree-node__channel tree-node__channel--root">
        <button
          v-if="hasChildren"
          type="button"
          class="tree-node__toggle"
          :aria-label="isExpanded ? $t('Свернуть') : $t('Раскрыть')"
          @click.stop="toggleExpand"
        >
          {{ isExpanded ? "−" : "+" }}
        </button>
      </div>

      <div
        v-if="showCheckbox && !isRoot"
        class="tree-node__checkbox"
        @click.stop
      >
        <Checkbox
          :model-value="isChecked"
          @update:model-value="onCheckChange"
        />
      </div>

      <div class="tree-node__label" :title="label">
        {{ label }}
      </div>
    </div>

    <div v-if="hasChildren && isExpanded" class="tree-node__children">
      <TreeNode
        v-for="(child, idx) in children"
        :key="child[nodeKey]"
        :node="child"
        :depth="depth + 1"
        :parent-lines="childParentLines"
        :is-last="idx === children.length - 1"
        :node-key="nodeKey"
        :label-key="labelKey"
        :children-key="childrenKey"
        :selected-id="selectedId"
        :checked-ids="checkedIds"
        :expanded-ids="expandedIds"
        :show-checkbox="showCheckbox"
        @select="onChildSelect"
        @toggle-expand="onChildToggleExpand"
        @toggle-check="onChildToggleCheck"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
$tree-line-color: hsl(var(--border));
$tree-channel-width: 24px;
$tree-line-offset: 14px;

.tree-node {
  width: 100%;

  &__row {
    display: flex;
    align-items: center;
    cursor: pointer;
    min-height: 36px;

    &--selected,
    &--root {
      .tree-node__label {
        @apply text-blue-500;
      }
    }

    &:hover {
      background-color: transparent;

      .tree-node__label {
        @apply text-blue-500;
      }
    }
  }

  &__channel {
    width: $tree-channel-width;
    flex-shrink: 0;
    position: relative;
    align-self: stretch;

    &--line::before {
      content: "";
      position: absolute;
      left: 50%;
      top: 0;
      bottom: 0;
      width: 1px;
      background: $tree-line-color;
      transform: translateX(-0.5px);
    }

    &--root {
      display: flex;
      align-items: center;
      justify-content: center;
      align-self: auto;
      height: 36px;
    }
  }

  &__line {
    position: absolute;
    background: $tree-line-color;

    &--v-top {
      left: 50%;
      top: 0;
      width: 1px;
      height: 50%;
      transform: translateX(-0.5px);
    }

    &--v-bottom {
      left: 50%;
      top: 50%;
      bottom: 0;
      width: 1px;
      transform: translateX(-0.5px);
    }

    &--h {
      left: 50%;
      top: 50%;
      right: 0;
      height: 1px;
      transform: translateY(-0.5px);
    }
  }

  &__toggle {
    width: 16px;
    height: 16px;
    background: hsl(var(--background));
    border: 1px solid hsl(var(--border));
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    color: hsl(var(--foreground));
    font-size: 12px;
    line-height: 1;
    font-weight: 400;

    &:hover {
      border-color: hsl(var(--foreground) / 0.5);
    }
  }

  &__channel--corner &__toggle {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }

  &__checkbox {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    padding: 0 6px 0 4px;
    height: 36px;
  }

  &__label {
    @apply flex items-center text-gray-600;
    flex: 1;
    min-width: 0;
    min-height: 36px;
    font-size: 14px;
    line-height: 1.4;
    padding: 0 4px;
    word-break: break-word;
  }

  &__children {
    width: 100%;
  }
}
</style>
